
import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { VerificationStatus } from '@/components/email-verification/VerificationStatus';

const EmailVerified = () => {
  const [verificationStatus, setVerificationStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [showOpenAppButton, setShowOpenAppButton] = useState(false);
  const [appOpenUrl, setAppOpenUrl] = useState<string | null>(null);
  const isNative = Capacitor.isNativePlatform();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const openNativeApp = async () => {
    if (isNative) {
      try {
        console.log('EmailVerified: Opening native app...');
        await Browser.open({
          url: 'mindnote://home',
          windowName: '_self'
        });
      } catch (error) {
        console.error('EmailVerified: Failed to open native app:', error);
      }
    }
  };

  // Build the custom scheme URL for opening the native iOS app
  const buildAppUrl = (accessToken?: string, refreshToken?: string, type?: string) => {
    const params = new URLSearchParams();
    if (accessToken) params.set('access_token', accessToken);
    if (refreshToken) params.set('refresh_token', refreshToken);
    if (type) params.set('type', type);
    return `mindnote://email-verified${params.toString() ? `?${params.toString()}` : ''}`;
  };

  // Attempt to open the native iOS app using custom scheme
  const attemptOpenNativeApp = (accessToken?: string, refreshToken?: string, type?: string) => {
    const isIOSDevice = /iPhone|iPad|iPod/i.test(navigator.userAgent) || 
      (navigator.userAgent.includes('Macintosh') && 'ontouchend' in document);
    if (!isIOSDevice) return;

    try {
      const customScheme = buildAppUrl(accessToken, refreshToken, type);
      console.log('EmailVerified: Trying custom scheme redirect:', customScheme);

      // Try multiple methods for reliability on iOS
      // Method 1: Direct location change (works in most cases)
      setTimeout(() => {
        window.location.href = customScheme;
      }, 300);

      // Method 2: Hidden iframe approach (fallback for some iOS versions)
      setTimeout(() => {
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = customScheme;
        document.body.appendChild(iframe);
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 2000);
      }, 100);
    } catch (err) {
      console.error('EmailVerified: Error attempting to open native app:', err);
    }
  };

  useEffect(() => {
    const processVerification = async () => {
      console.log('EmailVerified: Starting verification process');
      
      try {
        // Get tokens from URL hash (from Supabase auth callback)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const queryParams = new URLSearchParams(window.location.search);
        const accessToken = hashParams.get('access_token') || queryParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token') || queryParams.get('refresh_token');
        const tokenType = hashParams.get('type') || queryParams.get('type');

        console.log('EmailVerified: URL tokens analysis', {
          hasAccessToken: !!accessToken,
          hasRefreshToken: !!refreshToken,
          tokenType,
          fullHash: window.location.hash,
          currentUrl: window.location.href
        });

        // If we have verification tokens, process them
        if (accessToken && refreshToken) {
          console.log('EmailVerified: Processing email verification tokens');

          // Set the session FIRST so the user is authenticated
          const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (sessionError) {
            console.error('EmailVerified: Session error:', sessionError);
            setVerificationStatus('error');
            return;
          }

          if (sessionData?.user) {
            console.log('EmailVerified: User authenticated successfully:', sessionData.user.id);
            await handleReferral(sessionData.user.id);
            setVerificationStatus('success');

            // Clear the hash to prevent re-processing
            window.history.replaceState({}, document.title, window.location.pathname);

            // On iOS (browser or in-app browser from email), try to open native app AFTER session is set
            const isIOSDevice = /iPhone|iPad|iPod/i.test(navigator.userAgent) || 
              (navigator.userAgent.includes('Macintosh') && 'ontouchend' in document);
            if (isIOSDevice && !isNative) {
              const customSchemeUrl = buildAppUrl(accessToken, refreshToken, tokenType || 'signup');
              setAppOpenUrl(customSchemeUrl);
              setShowOpenAppButton(true);
              // Attempt automatic redirect to native app
              attemptOpenNativeApp(accessToken, refreshToken, tokenType || 'signup');
            } else {
              navigate('/');
            }
            return;
          }
        }

        // If no tokens in URL, check current session
        const { data: { session }, error: sessionCheckError } = await supabase.auth.getSession();
        
        if (sessionCheckError) {
          console.error('EmailVerified: Error checking session:', sessionCheckError);
          setVerificationStatus('error');
          return;
        }

        if (session?.user) {
          console.log('EmailVerified: User already has active session');
          await handleReferral(session.user.id);
          setVerificationStatus('success');
          
          // On iOS device, show "Open in App" button
          const isIOSDevice2 = !isNative && (/iPhone|iPad|iPod/i.test(navigator.userAgent) || 
            (navigator.userAgent.includes('Macintosh') && 'ontouchend' in document));
          if (isIOSDevice2) {
            const appUrl = buildAppUrl(session.access_token, session.refresh_token, 'session');
            setAppOpenUrl(appUrl);
            setShowOpenAppButton(true);
          } else {
            setTimeout(() => { navigate('/'); }, 2500);
          }
        } else {
          // No session found - redirect to sign in
          console.log('EmailVerified: No session found, redirecting to auth');
          setVerificationStatus('success');
          
          setTimeout(() => {
            if (isNative) {
              openNativeApp();
            } else {
              navigate('/auth');
            }
          }, 3000);
        }
      } catch (error) {
        console.error('EmailVerified: Verification process error:', error);
        setVerificationStatus('error');
      }
    };

    const handleReferral = async (userId: string) => {
      const pendingSignup = localStorage.getItem('pendingSignup');
      if (!pendingSignup) return;

      try {
        const { referralCode } = JSON.parse(pendingSignup);
        if (!referralCode) return;

        console.log('EmailVerified: Processing referral for code:', referralCode);
        const { data: referralLink, error: referralError } = await supabase
          .from('referral_links')
          .select('id, user_id')
          .eq('code', referralCode)
          .single();

        if (referralError) throw referralError;

        if (referralLink) {
          const { error: insertError } = await supabase
            .from('referrals')
            .insert([{
              referral_link_id: referralLink.id,
              referrer_id: referralLink.user_id,
              referred_id: userId,
            }]);

          if (insertError) throw insertError;
          console.log('EmailVerified: Referral successfully processed');
        }
      } catch (error) {
        console.error('EmailVerified: Error processing referral:', error);
      } finally {
        localStorage.removeItem('pendingSignup');
      }
    };

    processVerification();
  }, [navigate]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4">
      <div className="mb-8">
        <img 
          src="/lovable-uploads/32ad3d71-609b-4f99-b5d8-70c212e9146b.png" 
          alt="MindNote Logo" 
          className="h-25"
        />
      </div>
      
      <Card className="w-full max-w-md p-8 text-center space-y-6">
        <VerificationStatus 
          status={verificationStatus} 
          isNative={isNative} 
          showOpenAppButton={showOpenAppButton}
          onOpenApp={() => {
            if (appOpenUrl) {
              window.location.href = appOpenUrl;
            }
          }}
        />
      </Card>
    </div>
  );
};

export default EmailVerified;
